import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { BehaviorSubject, firstValueFrom, Observable, ReplaySubject } from "rxjs";
import { Injectable, signal } from "@angular/core";
import { Canal } from "@src/app/models/interfaces/canal.interface";
import { PlatformService } from "../platform/platform.service";
import { GlobalConstants } from "@src/app/global-constants";
import { Tag } from "@src/app/models/interfaces/tag.interface";
import { Noticia } from "@src/app/models/interfaces/noticia.interface";

@Injectable()
export class PortadaService {
  private readonly API_URL: any;
  private readonly isCartaEditorial = new BehaviorSubject<boolean | null>(null);
  public isCartaEditorial$: Observable<boolean> = this.isCartaEditorial.asObservable();
  private readonly canales$: ReplaySubject<Canal[]> = new ReplaySubject(1);
  canalesSignal = signal<Canal[] | null>(null);
  private readonly constantesGlobales: any;

  constructor(
    private readonly httpClient: HttpClient,
    private readonly platformService: PlatformService,
  ) {
    const host = this.platformService.getLocationHostname();
    this.constantesGlobales = new GlobalConstants(host);
    this.API_URL = this.constantesGlobales.urlApi;
  }

  /**
   * Obtiene la lista de canales desde la API según el ámbito proporcionado.
   *
   * @param idAmbito - El identificador del ámbito para filtrar los canales.
   * @returns Una promesa que no devuelve un valor directamente, pero actualiza el estado interno con los datos obtenidos.
   * @throws Lanza un error si ocurre algún problema al realizar la solicitud a la API.
   *
   * Este método realiza una solicitud HTTP GET a la API para obtener los canales relacionados
   * con el ámbito especificado. Los encabezados y parámetros necesarios se configuran
   * adecuadamente antes de realizar la solicitud. En caso de éxito, los datos obtenidos
   * se procesan y se almacenan mediante el método `setCanal`.
   */
  public async getCanalesApi(idAmbito: number): Promise<void> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });

    const body = new HttpParams().set("id_ambito", String(idAmbito));

    try {
      const observable = this.httpClient.get<any>(this.API_URL + "/listadoCanales", { headers: headers, params: body });
      const data = await firstValueFrom(observable);
      this.setCanal(data);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Establece el valor de los canales y actualiza el observable `canales$`.
   *
   * @param value - Un arreglo de objetos de tipo `Canal` que representa los nuevos canales a establecer.
   */
  public setCanal(value: Canal[]): void {
    this.canales$.next(value);
  }

  /**
   * Obtiene un observable que emite una lista de canales.
   *
   * @returns {Observable<Canal[]>} Un observable que emite un arreglo de objetos de tipo `Canal`.
   */
  public getCanal(): Observable<Canal[]> {
    return this.canales$.asObservable();
  }

  /**
   * Obtiene una lista de etiquetas desde la API.
   *
   * @returns {Observable<Tag[]>} Un observable que emite un arreglo de objetos `Tag`.
   *
   * @description
   * Este método realiza una solicitud HTTP GET al endpoint `/listadoTags` de la API
   * para recuperar una lista de etiquetas. Utiliza encabezados personalizados para
   * especificar el tipo de contenido y los formatos aceptados. En caso de éxito,
   * emite los datos recibidos y completa el observable. Si ocurre un error,
   * registra el error en la consola y emite el error a través del observable.
   *
   */
  public getTags(): Observable<Tag[]> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });

    return new Observable<Tag[]>((observer) => {
      this.httpClient.get<Tag[]>(this.API_URL + "/listadoTags", { headers: headers }).subscribe({
        next: (data) => {
          observer.next(data);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        },
      });
    });
  }

  /**
   * Obtiene una lista de videos en directo desde la API.
   *
   * @param numeroVideos - El número de videos a obtener. Si no se proporciona, se establece un valor predeterminado de 1.
   * @param idCanalDirecto - El identificador del canal directo. Puede ser `null`.
   * @returns Un observable que emite los datos de los videos obtenidos desde la API.
   *
   * @remarks
   * Este método realiza una solicitud HTTP GET a la URL configurada en `this.API_URL` con los parámetros proporcionados.
   * Si ocurre un error durante la solicitud, se registra en la consola y se emite un error a través del observable.
   */
  public getVideosDirecto(numeroVideos: number, idCanalDirecto: null): Observable<Noticia[]> {
    const headers = new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json, text/javascript, */*; q=0.01",
    });
    if (!numeroVideos) {
      numeroVideos = 1;
    }
    const body = new HttpParams().set("limit_videos", String(numeroVideos)).set("idCanalDirecto", idCanalDirecto);

    return new Observable<Noticia[]>((observer) => {
      this.httpClient.get<Noticia[]>(this.API_URL + "/videosDirecto", { headers: headers, params: body }).subscribe({
        next: (data) => {
          observer.next(data);
          observer.complete();
        },
        error: (error) => {
          observer.error(error);
        },
      });
    });
  }
}
